import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CalendarPlus, Ticket, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

export function TicketView() {
  const { reference } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/tickets/${reference}`).then((r) => setOrder(r.data)).catch(() => setErr(true));
  }, [reference]);

  if (err)
    return <div className="mx-auto max-w-lg px-4 py-32 text-center text-neutral-400">No ticket found for <span className="text-cyan-400">{reference}</span>.</div>;
  if (!order) return <div className="px-4 py-32 text-center text-neutral-500">Loading ticket…</div>;

  const paid = order.payment_status === "paid";

  return (
    <div className="mx-auto max-w-lg px-4 py-14 sm:py-20">
      <div className="overflow-hidden rounded-3xl border border-cyan-500/30 bg-[#0a0a0a] shadow-2xl" data-testid="digital-ticket">
        <div className="bg-cyan-500 px-6 py-4 text-center">
          <div className="font-display text-xl font-black uppercase tracking-wide text-black">EON Infinity Entertainment</div>
          <div className="font-mono-label text-[10px] uppercase tracking-[0.3em] text-black/70">Digital Admission</div>
        </div>
        <div className="p-6">
          <div className="text-center">
            <div className="inline-block rounded-2xl bg-white p-3">
              <img src={order.qr_data_uri} alt="QR code" className="h-44 w-44" data-testid="ticket-qr" />
            </div>
            <div className="mt-4 font-mono-label text-[11px] uppercase tracking-widest text-neutral-500">Reference</div>
            <div className="font-display text-3xl font-black tracking-wider text-cyan-400" data-testid="ticket-reference">{order.reference_code}</div>
            {!paid && <div className="mt-2 font-mono-label text-xs uppercase text-yellow-500">Payment {order.payment_status}</div>}
          </div>

          <div className="mt-6 space-y-2 border-t border-dashed border-neutral-700 pt-5 text-sm">
            {[
              ["Attendee", order.buyer_name],
              ["Event", order.event_title],
              ["Date", fmtDate(order.event_date)],
              ["Location", order.event_location],
              ["Tier", `${order.tier_name} × ${order.quantity}`],
              ["Paid", `$${Number(order.amount).toFixed(2)}`],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4">
                <span className="text-neutral-500">{k}</span>
                <span className="text-right font-medium text-stone-100">{v}</span>
              </div>
            ))}
          </div>

          <button
            onClick={() => toast.success("Add this to your calendar from the email we sent!")}
            data-testid="add-calendar-btn"
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full border border-neutral-700 py-3 font-display font-bold uppercase text-stone-200 hover:border-cyan-500 hover:text-cyan-400"
          >
            <CalendarPlus className="h-4 w-4" /> Add To Calendar
          </button>
        </div>
      </div>
      <p className="mt-6 text-center text-sm text-neutral-500">Show this QR / reference code at the gate. A copy is in your email.</p>
    </div>
  );
}

export function TicketLookup() {
  const [ref, setRef] = useState("");
  const navigate = useNavigate();
  const go = (e) => {
    e.preventDefault();
    if (!ref.trim()) return toast.error("Enter your reference code.");
    navigate(`/ticket/${ref.trim().toUpperCase()}`);
  };
  return (
    <div className="mx-auto max-w-md px-4 py-20 text-center">
      <Ticket className="mx-auto h-12 w-12 text-cyan-500" />
      <h1 className="mt-4 font-display text-4xl font-black uppercase text-cyan-50">Find My Ticket</h1>
      <p className="mt-3 text-neutral-400">Enter the reference code from your confirmation email (e.g. EON-TR-12345).</p>
      <form onSubmit={go} className="mt-8 flex gap-2">
        <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="EON-TR-XXXXX" data-testid="ticket-lookup-input" className="w-full rounded-full border border-neutral-700 bg-black px-5 py-3 text-center font-display text-lg uppercase tracking-wider text-stone-100 outline-none focus:border-cyan-500" />
        <button type="submit" data-testid="ticket-lookup-btn" className="rounded-full bg-cyan-500 px-6 font-display font-black uppercase text-black">Go</button>
      </form>
    </div>
  );
}

export function PaymentSuccess() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [reference, setReference] = useState(null);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) {
      setStatus("error");
      return;
    }
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setReference(data.reference_code);
          setStatus("paid");
          return;
        }
      } catch (e) {
        /* keep polling */
      }
      if (tries >= 8) {
        setStatus("pending");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, []);

  return (
    <div className="mx-auto max-w-md px-4 py-24 text-center">
      {status === "checking" && (
        <>
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-cyan-500" />
          <h1 className="mt-6 font-display text-3xl font-black uppercase text-cyan-50">Confirming Payment…</h1>
          <p className="mt-2 text-neutral-400">Hold tight, wrangling your tickets.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20 text-green-400 text-3xl">✓</div>
          <h1 className="mt-6 font-display text-4xl font-black uppercase text-cyan-50">You're In! 🤠</h1>
          <p className="mt-3 text-neutral-400">Tickets confirmed and emailed. Reference:</p>
          <div className="mt-2 font-display text-2xl font-black tracking-wider text-cyan-400" data-testid="success-reference">{reference}</div>
          <button onClick={() => navigate(`/ticket/${reference}`)} data-testid="view-ticket-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-cyan-500 px-8 py-4 font-display text-lg font-black uppercase text-black transition-transform hover:scale-105 active:scale-95">
            View Digital Ticket <ArrowRight className="h-5 w-5" />
          </button>
        </>
      )}
      {status === "pending" && (
        <>
          <h1 className="font-display text-3xl font-black uppercase text-cyan-50">Payment Processing</h1>
          <p className="mt-3 text-neutral-400">This is taking a moment. Check your email for the confirmation, or look up your ticket by reference.</p>
          <button onClick={() => navigate("/ticket-lookup")} className="mt-6 rounded-full border border-cyan-500 px-6 py-3 font-display font-bold uppercase text-cyan-400">Find My Ticket</button>
        </>
      )}
      {status === "error" && (
        <>
          <h1 className="font-display text-3xl font-black uppercase text-cyan-50">Something went sideways</h1>
          <button onClick={() => navigate("/events")} className="mt-6 rounded-full bg-cyan-500 px-6 py-3 font-display font-bold uppercase text-black">Back to Events</button>
        </>
      )}
    </div>
  );
}
